import { Button } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import EditIcon from "@material-ui/icons/Edit";
import Alert from "@material-ui/lab/Alert";
import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import useCurrentUser from "@qpr/client-api-base/context/UserContext";
import useLink from "@qpr/client-api-base/hooks/useLink";
import { GETWorkflowInstanceResultData } from "@qpr/client-api-workflow/types";
import useComments from "backend/hooks/comments/useComments";
import useBySymbol from "backend/hooks/elements/useBySymbol";
import useElement from "backend/hooks/elements/useElement";
import useFlatHierarchy from "backend/hooks/elements/useFlatHierarchy";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useAllWorkflowInstances from "backend/hooks/workflows/useAllWorkflowInstances";
import useDeleteWorkflowInstances from "backend/hooks/workflows/useDeleteWorkflowInstances";
import useElementWorkflows from "backend/hooks/workflows/useElementWorkflows";
import useStartElementWorkflow from "backend/hooks/workflows/useStartElementWorkflow";
import useSubmitElementValues from "backend/hooks/workflows/useSubmitElementValues";
import { ICardElementWithChildren } from "backend/model/model";
import sortHierarchy from "backend/utils/sortHierarchy";
import WorkflowAdminToolbar from "components/toolbars/WorkflowAdminToolbar";
import AdminButton from "design/components/buttons/AdminButton";
import PeriodChip from "design/components/chips/PeriodChip";
import ValueInputForm from "design/components/forms/ValueInputForm";
import GenericModal from "design/components/popups/GenericModal";
import CircularProgress from "design/components/progress/CircularProgress";
import MeasureTable from "design/components/tables/MeasureTable";
import ExecutiveDashboardTitle from "design/components/texts/ExecutiveDashboardTitle";
import ElementTreeView from "design/components/trees/ElementTreeView";
import RowView from "design/layouts/RowView";
import StackView from "design/layouts/StackView";
import TreeViewLeftPanel from "design/layouts/TreeViewLeftPanel";
import WhiteBgArea from "design/layouts/WhiteBgArea";
import Page from "design/pages/Page";
import React, { FunctionComponent } from "react";
interface ConfigurableReportsProps {}

const WorkflowAdmin: FunctionComponent<ConfigurableReportsProps> = () => {
  const ctx = useAppContext();
  const user = useCurrentUser();

  const link = useLink();
  const { data: rootItem } = useElement(ctx.elementId);

  const { isLoading: isCreating, mutate } = useStartElementWorkflow();
  const { isLoading: isDeleting, mutate: deleteWorkflowInstances } =
    useDeleteWorkflowInstances();

  const { data: allInstances, isLoading: isAllInstancesLoading } =
    useAllWorkflowInstances("value", ctx.period || "");

  const divRef = React.useRef<HTMLDivElement>(null);
  const [action, setAction] = React.useState("");
  const [deleting, setDeleting] = React.useState(isDeleting);
  const [creating, setCreating] = React.useState(isCreating);
  const [fileType, setFileType] = React.useState("");
  const [isExporting, setIsExporting] = React.useState(false);
  const [fileSelectAnchor, setFileSelectAnchor] =
    React.useState<HTMLDivElement | null>(null);

  const [openedItem, setOpenedItem] = React.useState<
    undefined | ICardElementWithChildren
  >(undefined);
  const {
    data: comments,
    isLoading: isCommentsLoading,
    isRefetching: isCommentsRefetching,
  } = useComments({
    id: openedItem?.workflowInstance?.meaId || "",
    period: openedItem?.workflowInstance?.period || "",
  });
  const saveValues = useSubmitElementValues();
  const [selectedItems, setSelectedItems] = React.useState<
    ICardElementWithChildren[]
  >([]);
  const selectedKPIs = React.useMemo(
    () => selectedItems.filter((item) => item.typename === "KPI"),
    [selectedItems]
  );
  const selectedInitiatives = React.useMemo(
    () => selectedItems.filter((item) => item.typename === "Initiative"),
    [selectedItems]
  );
  const { data: theme } = useBySymbol("L0");
  const { data: corpo } = useElement(theme?.id);
  const {
    data,
    isLoading: isDataLoading,
    isError,
    isPreviousData,
    isRefetching,
  } = useHierarchy(theme?.id);

  const treeViewData = React.useMemo(() => {
    if (corpo && data) {
      return [
        corpo,
        ...sortHierarchy(data, (a, b) => a.symbol.localeCompare(b.symbol)),
      ];
    }
    return [];
  }, [corpo, data]);
  const { data: subElements, isLoading: isSubElementsLoading } =
    useFlatHierarchy(rootItem?.topelement);

  const listItems = React.useMemo(() => {
    return (
      subElements
        ?.filter((el) => el.typename === "KPI" || el.typename === "Initiative")
        .sort((a, b) => a.name.localeCompare(b.name)) || []
    );
  }, [subElements]);

  const instancesPerElement = React.useMemo(() => {
    const obj: Record<
      string,
      Array<GETWorkflowInstanceResultData & ICardElementWithChildren>
    > = {};
    if (allInstances) {
      allInstances.forEach((inst) => {
        if (!obj[inst.meaId]) obj[inst.meaId] = [];
        obj[inst.meaId].push(inst);
      });
    }
    return obj;
  }, [data, allInstances, isDeleting, isCreating, saveValues.isLoading]);

  const {
    data: elementWorkflows,
    isLoading: isElementWorkflowsLoading,
    isRefetching: isElementWorkflowsRefetching,
  } = useElementWorkflows(
    listItems.map((item) => item.id),
    "value"
  );

  const toggleItem = (item: ICardElementWithChildren) => {
    setSelectedItems((items) => {
      if (items.find((it) => it.id === item.id)) {
        return items.filter((i) => i.id !== item.id);
      }
      return [...items, item];
    });
  };

  if (
    deleting ||
    isAllInstancesLoading ||
    isElementWorkflowsLoading ||
    isSubElementsLoading ||
    isDataLoading ||
    isDeleting ||
    isCreating ||
    isCommentsLoading ||
    saveValues.isLoading
  )
    return <CircularProgress />;

  const getElementInstances = (
    id: string
  ): (GETWorkflowInstanceResultData & ICardElementWithChildren)[] => {
    try {
      return instancesPerElement[id];
    } catch (e) {}
    return [];
  };

  return (
    <Page
      isLoading={
        !!!data ||
        isAllInstancesLoading ||
        isDeleting ||
        isCreating ||
        saveValues.isLoading
      }
      Toolbar={WorkflowAdminToolbar}
    >
      <GenericModal
        open={!!openedItem}
        onClose={() => {
          setOpenedItem(undefined);
        }}
      >
        <h1>
          {openedItem?.name} {openedItem?.workflowInstance?.period}{" "}
        </h1>
        {isCommentsLoading || isCommentsRefetching ? null : (
          <ValueInputForm
            buttons={[
              {
                tag: "submit",
                style: "submit",
                text: "Submit",
                onClick: (values) => {
                  console.log("Submit", values);
                  setOpenedItem(undefined);
                  saveValues.mutate({
                    fields: values,
                  });
                },
              },
            ]}
            key={`${
              openedItem?.workflowInstance?.wfInstanceId
            } ${JSON.stringify(comments)}`}
            element={openedItem!}
            onIntermediateChange={(values) => {
              console.log(values);
            }}
            fields={[
              {
                series: "ACT",
                element: openedItem,
                inputType: "string",
                isDisabled:
                  openedItem?.workflowInstance?.currentState !== "dataentry",
              },
              {
                title: "Actions and Recommendations",
                element: openedItem,
                inputType: "comment",
                comments: comments,
                initialValue:
                  comments && comments[0] ? comments[0].description : "",
              },
            ]}
          />
        )}
      </GenericModal>
      <StackView gap="0px">
        <RowView partWidths={["300px", "900px"]} justifyContent="flex-start">
          <TreeViewLeftPanel>
            <ElementTreeView
              selectedItem={rootItem}
              items={treeViewData}
              onSelected={(item) => {
                link({ meaId: item.id });
              }}
            />
          </TreeViewLeftPanel>
          <WhiteBgArea width="100%">
            <ExecutiveDashboardTitle>{rootItem?.name}</ExecutiveDashboardTitle>

            {!listItems ||
            listItems.length === 0 ||
            isElementWorkflowsLoading ||
            isElementWorkflowsRefetching ||
            saveValues.isLoading ||
            isRefetching ? (
              ctx.elementId ? (
                <CircularProgress />
              ) : (
                <Alert>Please select a scorecard</Alert>
              )
            ) : (
              <div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      flexDirection: "row",
                    }}
                  >
                    <AdminButton
                      onClick={() => {
                        listItems.forEach((elem) => {
                          toggleItem(elem);
                        });
                      }}
                    >
                      Toggle All
                    </AdminButton>
                    <AdminButton
                      onClick={() => {
                        listItems.forEach((elem) => {
                          setSelectedItems([]);
                        });
                      }}
                    >
                      Reset All
                    </AdminButton>
                    {selectedItems.length > 0 ? (
                      <>
                        <select
                          style={{
                            fontSize: "14px",
                            padding: "4px",
                            margin: "2px",
                          }}
                          value={action}
                          onChange={(e) => {
                            setAction(e.target.value);
                          }}
                        >
                          <option value="">-</option>
                          <option value="start_workflow">Start Workflow</option>
                          <option value="delete_workflow">
                            Delete Workflow
                          </option>
                        </select>
                        {action ? (
                          <AdminButton
                            onClick={() => {
                              if (action === "start_workflow") {
                                mutate({
                                  period: ctx.period || "",
                                  elementIds: selectedItems.map((el) => el.id),
                                });
                              }
                              if (action === "delete_workflow") {
                                const ids: string[] = [];
                                selectedItems.forEach((item: any) => {
                                  const wf = getElementInstances(item.id);
                                  if (wf && wf.length && wf[0].wfInstanceId)
                                    ids.push(wf[0].wfInstanceId);
                                });
                                deleteWorkflowInstances(ids);
                              }
                            }}
                          >
                            Execute
                          </AdminButton>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>

                <MeasureTable
                  items={listItems || []}
                  columns={[
                    {
                      title: (row) => {
                        return <div />;
                      },
                      value: (row) => (
                        <div>
                          <Checkbox
                            checked={
                              !!selectedItems.find(
                                (el) => el.id === row.elem.id
                              )
                            }
                            onChange={(value) => {
                              /** TODO */
                              toggleItem(row.elem);
                            }}
                          />
                        </div>
                      ),
                    },
                    {
                      title: "Name",
                      value: (row) => <div>{row.elem.name}</div>,
                    },
                    {
                      title: "ACT",
                      value: (row) => <div>{row.elem.ACT}</div>,
                    },
                    {
                      title: "Status",
                      sortFn: (a, b) => {
                        return Number(a.STATUS || 0) - Number(b.STATUS || 0);
                      },
                      value: (row) => (
                        <div>
                          {(() => {
                            switch (row.elem.STATUS) {
                              case "0":
                                return "Values not approved";
                              case "1":
                                return "Workflow completed";
                              default:
                                return "-";
                            }
                          })()}
                        </div>
                      ),
                    },
                    {
                      title: "Type",
                      value: (row) => <div>{row.elem.typename}</div>,
                    },
                    {
                      title: "Workflows",
                      value: (row) => {
                        if (!elementWorkflows) {
                          return <div>-</div>;
                        }
                        const items = getElementInstances(row.elem.id);
                        return (
                          <div>
                            {(items || []).map((wfItem) => {
                              return (
                                <PeriodChip
                                  title={`${wfItem.type} (${wfItem.currentState}) `}
                                  icon={row.elem.typename as any}
                                  period={wfItem?.period || ""}
                                >
                                  <Button
                                    onClick={() => {
                                      setOpenedItem(wfItem);
                                    }}
                                  >
                                    <EditIcon />
                                  </Button>
                                </PeriodChip>
                              );
                            })}
                          </div>
                        );
                      },
                    },
                  ]}
                />
              </div>
            )}
          </WhiteBgArea>
        </RowView>
      </StackView>
    </Page>
  );
};

export default WorkflowAdmin;
